/**
 * AdminChatPanel - Modal/Panel for admin to chat directly with a user
 * Used in TalentDetails and ClientDetail pages
 */

import { useState, useEffect, useRef } from 'react'
import { X, Send, Loader2, MessageCircle, Paperclip, CornerDownRight, Flag } from 'lucide-react'
import { useAdminMessages } from '../../hooks/useAdminMessages'
import AttachmentPreview from '../chat/AttachmentPreview'
import { ATTACHMENT_ACCEPT, validateAttachment, visibleMessageText, formatBytes } from '../../lib/messages/attachments.js'
import { format } from 'date-fns'

interface AdminChatPanelProps {
  userId: string
  userName: string
  userRole: string
  onClose: () => void
  /** New console's two-pane Messages: render in place (no overlay). The current console never passes it. */
  embedded?: boolean
  /** Routing (Traci, 21 Oct 2026): a "Route this" button on each message, to attach that message to the routing. Text
   *  highlighted in the message is what gets quoted, so the department reads only the part they are asked about. */
  onPickMessage?: (m: { id: string; text: string; from: string; at: string }) => void
  /** Scroll to this message and highlight it (the routed callout's "Go to message") */
  highlightId?: string | null
  /** Messages a routing points at: a flag on each ("Routed to Editing"), with the why on hover */
  markers?: Record<string, Array<{ label: string; why?: string | null }>>
}

export default function AdminChatPanel({
  userId,
  userName,
  userRole,
  onClose,
  embedded = false,
  onPickMessage,
  highlightId = null,
  markers
}: AdminChatPanelProps) {
  const {
    messages,
    isLoading,
    error,
    isConnected,
    sendMessage,
    markAsRead
  } = useAdminMessages(userId)

  const [messageText, setMessageText] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  // The text highlighted inside a message when "Route this" is pressed (read on mousedown, before the click moves focus)
  const pickedText = useRef('')
  const selectionIn = (messageId: string) => {
    const sel = typeof window !== 'undefined' ? window.getSelection() : null
    const text = sel ? sel.toString().trim() : ''
    const node = sel?.anchorNode ? (sel.anchorNode.nodeType === 1 ? sel.anchorNode as Element : sel.anchorNode.parentElement) : null
    return text && node?.closest(`[data-message-id="${messageId}"]`) ? text : ''
  }

  // Get current admin user ID (for message alignment)
  // Must use supabaseAdmin which has the admin's auth session (storageKey: 'sb-admin-auth')
  // The regular supabase client uses a different session (storageKey: 'lovep-auth')
  const [adminId, setAdminId] = useState<string | null>(null)
  useEffect(() => {
    import('../../integrations/supabase/client').then(({ supabaseAdmin }) => {
      supabaseAdmin.auth.getUser().then(({ data }) => {
        if (data.user) {
          setAdminId(data.user.id)
        }
      })
    })
  }, [])

  // Auto-scroll to the newest message inside the thread box only. scrollIntoView also scrolled the whole page, so the
  // console's Messages tab jumped to the bottom of a long chat (Nautica, 3 Oct 2026).
  useEffect(() => {
    const el = listRef.current
    if (!el) return
    // A routed message to go to wins over the newest one (scrolls inside the thread box only, like above)
    const target = highlightId ? el.querySelector<HTMLElement>(`[data-message-id="${highlightId}"]`) : null
    if (target) el.scrollTop = Math.max(0, target.offsetTop - el.offsetTop - 48)
    else el.scrollTop = el.scrollHeight
  }, [messages, highlightId])

  // Mark all unread messages as read when panel opens
  useEffect(() => {
    if (!adminId || messages.length === 0) return

    const unreadMessages = messages.filter(
      msg => msg.sender_id !== adminId && !msg.is_read_admin
    )

    if (unreadMessages.length === 0) return

    const markUnread = async () => {
      for (const msg of unreadMessages) {
        await markAsRead(msg.id)
      }
      // Notify the admin NotificationBell to clear notifications for this user only
      window.dispatchEvent(new CustomEvent('admin-messages-read', { detail: { userId } }))
    }

    const timer = setTimeout(markUnread, 500)
    return () => clearTimeout(timer)
  }, [adminId, messages, markAsRead])

  const handleSend = async () => {
    if ((!messageText.trim() && !file) || isSending) return

    setIsSending(true)
    try {
      const result = await sendMessage(messageText, file)
      if (result.success) {
        setMessageText('')
        setFile(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
      } else {
        alert(`Failed to send: ${result.error}`)
      }
    } finally {
      setIsSending(false)
    }
  }

  const handlePickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] || null
    if (!picked) return
    const check = validateAttachment(picked)
    if (!check.ok) {
      alert(check.error)
      e.target.value = ''
      return
    }
    setFile(picked)
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className={embedded ? 'h-full' : 'fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'}>
      <div className={embedded ? 'bg-white rounded-xl border h-[min(720px,calc(100vh-10rem))] min-h-[420px] flex flex-col' : 'bg-white rounded-lg shadow-2xl w-full max-w-2xl h-[600px] flex flex-col'}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Admin Chat with {userName}
            </h2>
            <p className="text-sm text-gray-500 capitalize">
              {userRole}
              {!isConnected && (
                <span className="ml-2 text-amber-600">• Connecting...</span>
              )}
              {isConnected && (
                <span className="ml-2 text-green-600">• Connected</span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages Area */}
        <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto p-6 bg-gray-50">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full text-red-500 text-sm">
              Error: {error}
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <MessageCircle className="w-16 h-16 mb-4 text-gray-300" />
              <p className="font-medium text-gray-600">No messages yet</p>
              <p className="text-sm mt-1">Start the conversation with {userName}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => {
                const isOwnMessage = message.sender_id === adminId
                const messageTime = format(new Date(message.created_at), 'MMM d, h:mm a')

                return (
                  <div
                    key={message.id}
                    data-message-id={message.id}
                    className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} ${highlightId === message.id ? 'rounded-lg ring-2 ring-red-400 ring-offset-4 ring-offset-gray-50' : ''}`}
                  >
                    <div className={`max-w-[75%] ${isOwnMessage ? 'items-end' : 'items-start'} flex flex-col`}>
                      {/* Sender name */}
                      <div className="text-xs text-gray-500 mb-1 px-3">
                        {isOwnMessage ? 'You (Admin)' : message.sender.full_name}
                      </div>
                      {/* Routed here (Trera, 21 Oct 2026: "some sort of tagged here or flag icon ... an indicator") */}
                      {(markers?.[message.id] || []).map((mk, i) => (
                        <span key={i} title={mk.why || undefined} className="mb-1 mx-3 inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                          <Flag className="h-3 w-3" aria-hidden="true" /> {mk.label}
                        </span>
                      ))}

                      {/* Message bubble */}
                      <div
                        className={`rounded-lg px-4 py-2 ${
                          isOwnMessage
                            ? 'bg-primary text-white'
                            : 'bg-white border border-gray-200 text-gray-900'
                        }`}
                      >
                        {message.attachment_url && (
                          <div className="mb-2">
                            <AttachmentPreview url={message.attachment_url} type={message.attachment_type || ''} name={message.attachment_name || 'attachment'} isOwnMessage={isOwnMessage} />
                          </div>
                        )}
                        {visibleMessageText(message) && (
                          <div className="whitespace-pre-wrap break-words">
                            {visibleMessageText(message)}
                          </div>
                        )}
                        <div
                          className={`text-xs mt-1 ${
                            isOwnMessage ? 'text-white/70' : 'text-gray-400'
                          }`}
                        >
                          {messageTime}
                        </div>
                      </div>
                      {onPickMessage && (
                        <button
                          type="button"
                          onMouseDown={() => { pickedText.current = selectionIn(message.id) }}
                          onClick={() => {
                            const quote = pickedText.current || selectionIn(message.id)
                            pickedText.current = ''
                            onPickMessage({ id: message.id, text: quote || visibleMessageText(message) || message.attachment_name || '(attachment)', from: isOwnMessage ? 'Staff' : message.sender?.full_name || userName, at: message.created_at })
                          }}
                          className="mt-0.5 inline-flex items-center gap-1 px-3 text-[11px] text-gray-500 hover:text-primary hover:underline"
                          aria-label={`Route this message: ${(visibleMessageText(message) || '').slice(0, 40)}`}
                        >
                          <CornerDownRight className="h-3 w-3" aria-hidden="true" /> Route this
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-200 bg-white p-4">
          {file && (
            <div className="mb-2 flex items-center gap-2 text-xs text-gray-600">
              <Paperclip className="w-3.5 h-3.5" />
              <span className="truncate">{file.name}</span>
              <span className="text-gray-400">{formatBytes(file.size)}</span>
              <button type="button" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = '' }} className="ml-1 text-gray-400 hover:text-gray-600" aria-label="Remove attachment"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          <div className="flex items-end gap-3">
            <input ref={fileInputRef} type="file" accept={ATTACHMENT_ACCEPT} onChange={handlePickFile} className="hidden" aria-label="Attach a file" />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSending || !isConnected}
              className="h-[76px] px-3 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Attach a photo or PDF (10 MB)"
              aria-label="Attach a file"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Send a message to ${userName}...`}
              disabled={isSending || !isConnected}
              rows={2}
              className="flex-1 resize-none border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
            />
            <button
              onClick={handleSend}
              disabled={isSending || (!messageText.trim() && !file) || !isConnected}
              className="bg-primary hover:bg-primary-600 text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 h-[76px]"
            >
              {isSending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>Send</span>
                </>
              )}
            </button>
          </div>
          <div className="mt-2 text-xs text-gray-400">
            Press Enter to send, Shift+Enter for new line · attach JPEG, PNG or PDF up to 10 MB
          </div>
        </div>
      </div>
    </div>
  )
}
